import React, { useEffect, useState, useMemo } from "react";
import withRouter from "components/Common/withRouter";
import TableContainer from "components/Common/TableContainer";
import {
  Card, CardBody, Col, Container, Row, Badge, Spinner, Button,
  Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import axios from "axios";
import moment from "moment";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const API_BASE = "https://maasradarapi.stechomeyazilim.info";

const UsersList = () => {
  document.title = "Kullanıcılar | Maaş Radar Admin Paneli";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  const [detailModal, setDetailModal] = useState(false);
  const [detailUser, setDetailUser] = useState(null);

  const [editModal, setEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});

  function getAuthHeaders() {
    const token = localStorage.getItem("token");
    return { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
  }

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/Users?$orderby=CreatedAt desc&$top=200`, { headers: getAuthHeaders() });
      setUsers(res.data.value || []);
    } catch (error) {
      console.error("Kullanıcı verileri alınamadı", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const showToast = (success, message) => {
    toastr.options = { positionClass: "toast-top-right", timeOut: 5000, closeButton: true, progressBar: true };
    if (success) toastr.success(message, "Başarılı");
    else toastr.error(message, "Hata");
  };

  const handleDelete = (id) => {
    if (!window.confirm("Bu kullanıcıyı silmek istediğinize emin misiniz?")) return;
    setProcessing(p => ({ ...p, [id + "delete"]: true }));
    axios.patch(`${API_BASE}/Users(${id})`, { IsDeleted: true }, { headers: getAuthHeaders() })
      .then(() => { showToast(true, "Kullanıcı silindi."); fetchUsers(); })
      .catch((err) => { console.error(err); showToast(false, "Silme sırasında hata oluştu."); })
      .finally(() => setProcessing(p => ({ ...p, [id + "delete"]: false })));
  };

  const openDetail = (user) => { setDetailUser(user); setDetailModal(true); };

  const openEdit = (user) => {
    setEditUser(user);
    setEditForm({
      IsActive: user.IsActive !== false,
      IsPremium: user.IsPremium === true,
      IsEmailVerified: user.IsEmailVerified === true,
      IsPhoneVerified: user.IsPhoneVerified === true,
      UserType: user.UserType || "Kullanici",
    });
    setEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    setProcessing(p => ({ ...p, [editUser.Id + "save"]: true }));
    try {
      await axios.patch(`${API_BASE}/Users(${editUser.Id})`, editForm, { headers: getAuthHeaders() });
      showToast(true, "Kullanıcı güncellendi.");
      setEditModal(false);
      fetchUsers();
    } catch (error) {
      console.error("Güncelleme hatası:", error);
      showToast(false, "Güncelleme sırasında hata oluştu.");
    } finally {
      setProcessing(p => ({ ...p, [editUser.Id + "save"]: false }));
    }
  };

  const userTypeBadge = (type) => {
    if (type === "Admin") return <Badge color="danger">Admin</Badge>;
    if (type === "Isveren") return <Badge color="success">İşveren</Badge>;
    return <Badge color="info">Kullanıcı</Badge>;
  };

  const columns = useMemo(() => [
    { Header: "ID", accessor: "Id", width: "80px" },
    { Header: "Ad Soyad", accessor: "FullName" },
    { Header: "E-posta", accessor: "Email" },
    { Header: "Telefon", accessor: "Phone" },
    { Header: "Tip", accessor: "UserType", Cell: ({ value }) => userTypeBadge(value) },
    { Header: "Premium", accessor: "IsPremium", Cell: ({ value }) => <Badge color={value ? "warning" : "light"}>{value ? "Evet" : "Hayır"}</Badge> },
    { Header: "Aktif", accessor: "IsActive", Cell: ({ value }) => <Badge color={value !== false ? "success" : "danger"}>{value !== false ? "Evet" : "Hayır"}</Badge> },
    { Header: "Kayıt Tarihi", accessor: "CreatedAt", Cell: ({ value }) => value ? moment(value).format("DD.MM.YYYY") : "-" },
    {
      Header: "İşlemler",
      accessor: "actions",
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button size="sm" color="info" onClick={() => openDetail(row.original)}>Detay</Button>
          <Button size="sm" color="primary" onClick={() => openEdit(row.original)}>Düzenle</Button>
          <Button size="sm" color="danger" disabled={processing[row.original.Id + "delete"]} onClick={() => handleDelete(row.original.Id)}>
            {processing[row.original.Id + "delete"] ? <Spinner size="sm" /> : "Sil"}
          </Button>
        </div>
      )
    }
  ], [processing]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Kullanıcılar" breadcrumbItem="Liste" />
          <Row><Col lg="12">
            <Card><CardBody>
              {loading ? (
                <div className="text-center py-5"><Spinner color="primary" /></div>
              ) : (
                <TableContainer columns={columns} data={users} isGlobalFilter={true} isAddOptions={false} customPageSize={10} className="custom-header-css" />
              )}
            </CardBody></Card>
          </Col></Row>
        </Container>
      </div>

      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="lg">
        <ModalHeader toggle={() => setDetailModal(false)}>Kullanıcı Detayı</ModalHeader>
        <ModalBody>
          {detailUser && (
            <div className="row">
              <div className="col-md-6">
                <p><strong>ID:</strong> {detailUser.Id}</p>
                <p><strong>Ad Soyad:</strong> {detailUser.FullName || "-"}</p>
                <p><strong>E-posta:</strong> {detailUser.Email || "-"}</p>
                <p><strong>Telefon:</strong> {detailUser.Phone || "-"}</p>
                <p><strong>Kullanıcı Tipi:</strong> {userTypeBadge(detailUser.UserType)}</p>
                <p><strong>Premium:</strong> {detailUser.IsPremium ? "Evet" : "Hayır"}</p>
                <p><strong>Bio:</strong> {detailUser.Bio || "-"}</p>
                <p><strong>LinkedIn:</strong> {detailUser.LinkedInUrl || "-"}</p>
              </div>
              <div className="col-md-6">
                <p><strong>Aktif:</strong> {detailUser.IsActive !== false ? "Evet" : "Hayır"}</p>
                <p><strong>Email Doğrulandı:</strong> {detailUser.IsEmailVerified ? "Evet" : "Hayır"}</p>
                <p><strong>Telefon Doğrulandı:</strong> {detailUser.IsPhoneVerified ? "Evet" : "Hayır"}</p>
                <p><strong>Maaş Doğrulandı:</strong> {detailUser.IsSalaryVerified ? "Evet" : "Hayır"}</p>
                <p><strong>Anonim Profil:</strong> {detailUser.IsAnonymousProfile ? "Evet" : "Hayır"}</p>
                <p><strong>Yetenekler:</strong> {detailUser.Skills || "-"}</p>
                <p><strong>Eğitim:</strong> {detailUser.Education || "-"}</p>
                <p><strong>Kayıt Tarihi:</strong> {detailUser.CreatedAt ? moment(detailUser.CreatedAt).format("DD.MM.YYYY HH:mm") : "-"}</p>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={editModal} toggle={() => setEditModal(false)}>
        <ModalHeader toggle={() => setEditModal(false)}>Kullanıcı Düzenle</ModalHeader>
        <ModalBody>
          {editUser && (
            <Form>
              <FormGroup><Label>Kullanıcı Tipi</Label>
                <Input type="select" value={editForm.UserType} onChange={e => setEditForm({...editForm, UserType: e.target.value})}>
                  <option value="Kullanici">Kullanıcı</option>
                  <option value="Isveren">İşveren</option>
                  <option value="Admin">Admin</option>
                </Input>
              </FormGroup>
              <FormGroup check className="mb-2"><Label check><Input type="checkbox" checked={editForm.IsActive} onChange={e => setEditForm({...editForm, IsActive: e.target.checked})} /> Aktif</Label></FormGroup>
              <FormGroup check className="mb-2"><Label check><Input type="checkbox" checked={editForm.IsPremium} onChange={e => setEditForm({...editForm, IsPremium: e.target.checked})} /> Premium</Label></FormGroup>
              <FormGroup check className="mb-2"><Label check><Input type="checkbox" checked={editForm.IsEmailVerified} onChange={e => setEditForm({...editForm, IsEmailVerified: e.target.checked})} /> Email Doğrulandı</Label></FormGroup>
              <FormGroup check className="mb-3"><Label check><Input type="checkbox" checked={editForm.IsPhoneVerified} onChange={e => setEditForm({...editForm, IsPhoneVerified: e.target.checked})} /> Telefon Doğrulandı</Label></FormGroup>
              <Button color="primary" disabled={processing[editUser.Id + "save"]} onClick={handleSaveEdit}>
                {processing[editUser.Id + "save"] ? <Spinner size="sm" /> : "Kaydet"}
              </Button>
            </Form>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default withRouter(UsersList);
